import * as React from 'react';
import { Image, Text, View, TextStyle } from 'react-native';

import { InstallationStep } from '../../../lib/mobileSafariExtension/mobileSafariExtension';
import { getLocalizedTexts } from '../../../Locales';
import { newDesignSystemFontStyles, normalizeHeight, styles } from './Styles';
import HighlightedText from '../../common/HighlightedText';
import color from '../../../style/color';
import { redirectToPrivacyPolicy } from './RedirectLinks';
import { Stepper } from '../../../pages/public/MobileSafariExtensionInstallationPagesRevamped';
import { VerticalStepper } from './VerticalStepper';

export const COPY_VARIANT = {
    NEW_COPY: 'newCopy',
} as const;
export type CopyVariant = (typeof COPY_VARIANT)[keyof typeof COPY_VARIANT];

const logo = { uri: '/assets/images/logos/lightning-square.svg' };
const warningIcon = { uri: '/assets/images/icons/warning.svg' };

export function Header({
    step,
    shouldDisplayStepper,
    titleStyle,
    shouldUseVerticalStepper,
    copyVariant,
}: {
    step: InstallationStep;
    shouldDisplayStepper: boolean | undefined;
    titleStyle?: TextStyle;
    shouldUseVerticalStepper?: boolean;
    copyVariant?: CopyVariant;
}) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    const verticalStepperLabels =
        copyVariant === COPY_VARIANT.NEW_COPY
            ? { left: texts.header.newCopyLeft, right: texts.header.newCopyRight }
            : undefined;

    function getStepNumber(step: InstallationStep) {
        if (step === InstallationStep.activateStep) {
            return 0;
        } else if (
            step === InstallationStep.authorizeStep ||
            step === InstallationStep.authorizeWithBannerStep ||
            step === InstallationStep.missingAllWebsitesAuthorization ||
            step === InstallationStep.missingAllWebsitesAuthorizationAfterRetry
        ) {
            return 1;
        } else {
            return undefined;
        }
    }

    const stepNumber = getStepNumber(step);

    return (
        <View style={styles.containerHeader}>
            <Image {...getHeaderIconImage(step)} />
            {shouldDisplayStepper &&
                stepNumber !== undefined &&
                (shouldUseVerticalStepper ? (
                    <VerticalStepper currentStep={stepNumber} labels={verticalStepperLabels} />
                ) : (
                    <Stepper currentStep={stepNumber} />
                ))}
            <View style={styles.containerHeaderText}>
                <Text style={titleStyle ?? newDesignSystemFontStyles.titleXL}>{getTitleText(step, copyVariant)}</Text>
                <Subtitle step={step} copyVariant={copyVariant} />
            </View>
        </View>
    );
}

function Subtitle({ step, copyVariant }: { step: InstallationStep; copyVariant?: CopyVariant }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    const isNewCopy = copyVariant === COPY_VARIANT.NEW_COPY;
    switch (step) {
        case InstallationStep.authorizeStep:
        case InstallationStep.authorizeWithBannerStep:
            return (
                <AuthorizationSubtitle
                    text={isNewCopy ? texts.authorizeStep.newCopyHeaderSubtitle : texts.authorizeStep.headerSubtitle}
                    isNewCopy={isNewCopy}
                />
            );
        case InstallationStep.missingAllWebsitesAuthorization:
        case InstallationStep.missingAllWebsitesAuthorizationAfterRetry:
            return <AuthorizationSubtitle text={texts.missingAuthorization.headerSubtitle} />;
        case InstallationStep.conclusionStep:
            return (
                <Text style={[newDesignSystemFontStyles.paragraphSmall, { color: color.boulder }]}>
                    {texts.conclusion.headerSubtitle}
                </Text>
            );
        default:
            return null;
    }
}

function AuthorizationSubtitle({ text, isNewCopy }: { text: string; isNewCopy?: boolean }) {
    const baseStyle = isNewCopy ? newDesignSystemFontStyles.newCopySubtitleText : newDesignSystemFontStyles.paragraphXS;
    return (
        <HighlightedText
            style={[baseStyle, { color: color.boulder }]}
            highlightedStyle={{ textDecorationLine: 'underline', textUnderlineOffset: normalizeHeight(3) } as TextStyle}
            onPressHighlightedTexts={[redirectToPrivacyPolicy]}>
            {text}
        </HighlightedText>
    );
}

function getHeaderIconImage(step: InstallationStep) {
    switch (step) {
        case InstallationStep.missingAllWebsitesAuthorization:
        case InstallationStep.missingAllWebsitesAuthorizationAfterRetry:
            return { source: warningIcon, style: styles.warningHeaderIcon };
        default:
            return { source: logo, style: styles.logoHeaderIcon };
    }
}

function getTitleText(step: InstallationStep, copyVariant?: CopyVariant) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    const isNewCopy = copyVariant === COPY_VARIANT.NEW_COPY;
    switch (step) {
        case InstallationStep.activateStepPreamble:
        case InstallationStep.activateStep:
            return isNewCopy ? texts.activateStep.newCopyHeaderTitle : texts.activateStep.headerTitle;
        case InstallationStep.authorizeStep:
        case InstallationStep.authorizeWithBannerStep:
            return isNewCopy ? texts.authorizeStep.newCopyHeaderTitle : texts.authorizeStep.headerTitle;
        case InstallationStep.missingAllWebsitesAuthorization:
        case InstallationStep.missingAllWebsitesAuthorizationAfterRetry:
            return texts.missingAuthorization.headerTitle;
        case InstallationStep.conclusionStep:
            return texts.conclusion.headerTitle;
        default:
            throw new Error(`Invalid step: ${step}`);
    }
}
